import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Drawer, Box, Stack, Grid, Divider, Button, Typography } from '@mui/material'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import IdWindow from 'components/containers/customers/contacts/IdWindow'
import Loading from 'components/generic/Loading'
import { useUploadOrgImageMutation, useGetLicenseInfoMutation } from 'services/assets'
import { setSnackBar } from 'state/ui'
import { saveRequiredDocumentsState, setIsTempFolder } from 'state/customer/customerContactsSlice'
import useDuplicateCustomerCheck from 'utils/custom-hooks/useDuplicateCustomerCheck.jsx'

const ScanIdModal = ({ methods, isOpen, onHideModal, formPrefix }) => {
  const dispatch = useDispatch()
  const {
    contact: { requiredDocuments },
  } = useSelector(state => state.customerContacts)
  const { settings } = useSelector(state => state.settings)
  const { customerId } = useSelector(state => state.ui.formModalProps)
  const isUpdate = !!customerId
  const [initialRequiredDocuments, setInitialRequiredDocuments] = useState({})

  const { getValues, setValue } = methods
  const [frontImage, backImage] = getValues([`${formPrefix}licenseImage`, `${formPrefix}licenseImageBack`])

  const [uploadOrgImage, { isLoading: isUploadingOrgImage }] = useUploadOrgImageMutation()
  const [getLicenseInfo, { isLoading: isLoadingGetLicenseInfo }] = useGetLicenseInfoMutation()

  const duplicateCheckResult = useDuplicateCustomerCheck({
    onCloseModal: () => handleCloseModal(),
    onProceedWithNewCustomer: () => handleCloseModal(),
  })

  const { checkDuplicateCustomer, DuplicateCustomerModal, isLoadingCustomers } = isUpdate
    ? { checkDuplicateCustomer: () => {}, DuplicateCustomerModal: () => null, isLoadingCustomers: false }
    : duplicateCheckResult

  const isLoading = isUploadingOrgImage || isLoadingGetLicenseInfo || isLoadingCustomers

  const { handleSubmit, setValue: saveImage, reset: resetIdModal, formState } = useForm()
  const { isDirty, touchedFields } = formState

  const genderMap = {
    Male: 'M',
    Female: 'F',
  }

  // store initial isTemp details whenever modal opens, in case user clicks cancel
  useEffect(() => {
    if (isOpen === true) {
      setInitialRequiredDocuments(requiredDocuments)
    }
  }, [isOpen])

  const handleError = (error, message) => {
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: error?.data?.error || message,
      }),
    )
  }

  const handleCloseModal = () => {
    dispatch(saveRequiredDocumentsState(initialRequiredDocuments))
    resetIdModal()
    onHideModal()
  }

  const processBackImage = async backBlob => {
    if (!backBlob) {
      setValue(`${formPrefix}licenseImageBack`, '')
      return null
    }

    const backFormData = new FormData()
    backFormData.append('image', backBlob)

    // Step 1: Upload the image
    try {
      const uploadResponse = await uploadOrgImage({ body: backFormData }).unwrap()
      setValue(`${formPrefix}licenseImageBack`, uploadResponse?.imageName)
      dispatch(setIsTempFolder({ fileNameKey: 'licenseImageBack', isTemp: true }))
    } catch (error) {
      handleError(error, 'There was an error uploading your image')
      return null
    }

    // Step 2: Parse license info if upload successful
    try {
      const licenseResponse = await getLicenseInfo({ body: backFormData }).unwrap()
      if (licenseResponse?.ParseImageResult?.Confidence === 100) {
        const licenseData = licenseResponse?.ParseImageResult?.DriverLicense
        populateLicenseData(licenseData)

        return {
          shouldCheck: true,
          licenseData,
        }
      }
    } catch (error) {
      handleError(error, 'There was an error parsing your image')
    }

    return null
  }

  const populateLicenseData = licenseData => {
    setValue(`${formPrefix}address1`, licenseData?.Address1 || '')
    setValue(`${formPrefix}address2`, licenseData?.Address2 || '')
    setValue(`${formPrefix}birthDate`, licenseData?.Birthdate ? new Date(`${licenseData.Birthdate}T00:00:00`) : '')
    setValue(`${formPrefix}city`, licenseData?.City || '')
    setValue(`${formPrefix}eyes`, licenseData?.EyeColor || '')
    setValue(`${formPrefix}firstNameLegal`, licenseData?.FirstName || '')
    setValue(`${formPrefix}gender`, licenseData?.Gender ? genderMap[licenseData.Gender] : '')
    setValue(`${formPrefix}hair`, licenseData?.HairColor || '')
    setValue(`${formPrefix}height`, licenseData?.Height || '')
    setValue(`${formPrefix}idType`, 'license')
    setValue(`${formPrefix}lastNameLegal`, licenseData?.LastName || '')
    setValue(
      `${formPrefix}licenseExpiration`,
      licenseData?.ExpirationDate ? new Date(`${licenseData.ExpirationDate}T00:00:00`) : '',
    )
    setValue(`${formPrefix}licenseNumber`, licenseData?.LicenseNumber || '')
    setValue(`${formPrefix}state`, licenseData?.JurisdictionCode || '')
    setValue(`${formPrefix}zip`, licenseData?.PostalCode || '')

    if (settings.customerSettings.compliance.enableRaceAndWeightCollection) {
      const weightNumber = licenseData?.WeightLBS ? parseInt(licenseData.WeightLBS) : null
      setValue(`${formPrefix}race`, licenseData?.Race || '')
      setValue(`${formPrefix}weight`, weightNumber || null)
    }
  }

  const onSubmit = async data => {
    const { front: frontBlob, back: backBlob } = data
    let shouldRunDuplicateCheck = false
    let licenseDataForCheck = null

    try {
      // Handle front image
      if (touchedFields.front) {
        if (frontBlob) {
          const frontFormData = new FormData()
          frontFormData.append('image', frontBlob)
          try {
            const response = await uploadOrgImage({ body: frontFormData }).unwrap()
            setValue(`${formPrefix}licenseImage`, response?.imageName)
            dispatch(setIsTempFolder({ fileNameKey: 'licenseImage', isTemp: true }))
          } catch (error) {
            handleError(error, 'There was an error uploading your image')
          }
        } else {
          // if no blob, then delete image
          setValue(`${formPrefix}licenseImage`, '')
        }
      }

      // Handle back image processing
      if (touchedFields.back) {
        const result = await processBackImage(backBlob)
        if (result) {
          shouldRunDuplicateCheck = result.shouldCheck
          licenseDataForCheck = result.licenseData
        }
      }

      // Run duplicate check or close modal
      if (!isUpdate && shouldRunDuplicateCheck && licenseDataForCheck) {
        // Use our hook's checkDuplicateCustomer function
        const search = `${licenseDataForCheck.FirstName} ${licenseDataForCheck.LastName}`.trim()
        if (search) {
          checkDuplicateCustomer(search)
        }
      }
      handleCloseModal()
    } catch (error) {
      console.error('Error in onSubmit:', error)
      handleError({ data: { error: error.message || 'Unknown error' } }, 'An unexpected error occurred')
      handleCloseModal()
    }
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleCloseModal}
        PaperProps={{ sx: { maxWidth: 1024, width: '100%' } }}
        SlideProps={{
          direction: 'up',
        }}
      >
        <Box role="presentation">
          <form onSubmit={handleSubmit(onSubmit)}>
            {isLoading && <Loading />}
            <Stack height="100vh">
              <FormModalHeading title="Scan ID" backButtonPosition="right" onBack={handleCloseModal} />
              <Divider />
              <Box px={4} py={2} flexGrow={1}>
                <Grid container>
                  <Grid item xs={7}>
                    <Typography gutterBottom variant="h5">
                      Front of ID
                    </Typography>
                    <Typography>
                      Position the front of the ID within the frame. This photo will be stored in GreenSpark.
                    </Typography>
                  </Grid>
                  <IdWindow
                    side="front"
                    loading={isLoading}
                    saveImage={saveImage}
                    image={{
                      filename: frontImage,
                      isTemp: requiredDocuments?.licenseImage?.isTempFolder || false,
                    }}
                  />
                </Grid>
                <Grid container pt={2}>
                  <Grid item xs={7}>
                    <Typography gutterBottom variant="h5">
                      Back of ID
                    </Typography>
                    <Typography>
                      Position the back of the ID within the frame. This photo will be used to populate the information
                      fields on the Compliance Information screen and stored in GreenSpark.
                    </Typography>
                  </Grid>
                  <IdWindow
                    side="back"
                    loading={isLoading}
                    saveImage={saveImage}
                    image={{
                      filename: backImage,
                      isTemp: requiredDocuments?.licenseImageBack?.isTempFolder || false,
                    }}
                  />
                </Grid>
              </Box>
              <Divider />
              <Stack direction="row" p={2} spacing={2}>
                <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={!isDirty}>
                  Save ID
                </Button>
                <Button variant="outlined" onClick={handleCloseModal}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </form>
        </Box>
      </Drawer>

      {/* Only render the duplicate check modal when not in update mode */}
      {!isUpdate && <DuplicateCustomerModal />}
    </>
  )
}

ScanIdModal.propTypes = {
  methods: PropTypes.object.isRequired,
  isOpen: PropTypes.bool,
  onHideModal: PropTypes.func,
  formPrefix: PropTypes.string,
}

ScanIdModal.defaultProps = {
  isOpen: false,
  onHideModal: () => {},
  formPrefix: '',
}

export default ScanIdModal
